import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { useAccountCode, useAccountCodeStore } from "hooks";
import { AccountCode, AccountCodeForm, AccountCodeQuery } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type {
  QueryState,
} from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";

const defaultFormValues: AccountCodeForm = {
  code: "",
  name: "",
  type: "",
  description: "",
};

const AccountCodes: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<AccountCode | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<AccountCodeQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<AccountCode | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useAccountCodeStore();
  const { fetchList, createAccountCode, updateAccountCode, deleteAccountCode } =
    useAccountCode();

  const {
    data: accountCodeData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const {
    reset,
  } = useForm<AccountCodeForm>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (accountCodeData?.data) {
      setRows(accountCodeData.data as AccountCode[]);
    }
  }, [accountCodeData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createAccountCode.isPending ||
        updateAccountCode.isPending ||
        deleteAccountCode.isPending,
      ),
    );
  }, [
    isFetching,
    createAccountCode.isPending,
    updateAccountCode.isPending,
    deleteAccountCode.isPending,
    setLoading,
  ]);

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow('edit')
      setEditingId(params.data.id);
      reset({
        code: params.data.code,
        name: params.data.name,
        description: params.data.description || "",
      });

      setTimeout(() => gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      }), 300);
    },
    [reset, setEditingId],
  );

  const handleDeleteRow = useCallback((row: AccountCode) => {
    setConfirmRow(row);
    setShowInlineAddRow('')
  }, []);
  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteAccountCode.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
          reset(defaultFormValues);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa mã không thành công");
      },
    });
  }, [confirmRow, deleteAccountCode, editingId, removeRow, reset, setEditingId]);

  const columns = useMemo<ColDef<AccountCode>[]>(() => {
    return [
      {
        headerName: "Mã",
        field: "code",
        minWidth: 140,
      },
      {
        headerName: "Tên",
        field: "name",
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Mô tả",
        field: "description",
        flex: 1,
        minWidth: 200,
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<AccountCode>): AccountCodeForm => ({
      code: String(row.code ?? "").trim(),
      name: String(row.name ?? "").trim(),
      type: "", // removed type column; keep payload shape but empty
      description: row.description || "",
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };
  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      code: "",
      name: "",
      type: "",
      description: "",
    } as AccountCode;
    setAddRowVersion((v) => v + 1);
  }, []);
  const persistRow = useCallback(
    (row: Partial<AccountCode>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.code || !payload.name) {
        SnackbarUtils.error("Mã và Tên là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createAccountCode.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as AccountCode);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm mã không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được mã cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateAccountCode.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as AccountCode);
            } else {
              upsertRow({ ...(row as AccountCode), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createAccountCode,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateAccountCode,
      upsertRow,
    ],
  );



  const handleSaveRow = useCallback(
    (row: AccountCode, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
      setShowInlineAddRow('')
    },
    [persistRow],
  );

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );


  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );
  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Quản lý mã tài khoản</h1>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách mã tài khoản
            </h2>
            {/* <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p> */}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          totalPages={accountCodeData?.totalPage}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow('');
            setEditingId(null)
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách mã tài khoản, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa mã tài khoản?"
        description={`Bạn chắc chắn muốn xóa mã "${confirmRow?.code ?? ""}"${confirmRow?.name ? ` - ${confirmRow.name}` : ""}? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteAccountCode.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default AccountCodes;
